import React, { useState, useEffect } from 'react';
import { 
    Users, 
    MessageSquare, 
    ChevronDown,
    ChevronUp,
    Plus, 
    Trash2, 
    Save, 
    Target,
    Filter,
    Calendar,
    Phone,
    User,
    Settings as SettingsIcon,
    RefreshCw,
    Bot,
    Search,
    CheckCircle2,
    PlusCircle,
    Layers,
    Sparkles,
    Cpu,
    ExternalLink,
    MapPin,
    Briefcase,
    TrendingUp,
    Check,
    Send,
    LayoutGrid,
    List as ListIcon
} from 'lucide-react';
import { 
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import LeadModal from '../components/LeadModal';

const CRM = () => {
    const { user: currentUser } = useAuth();
    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLeadId, setExpandedLeadId] = useState(null);
    const [activeTab, setActiveTab] = useState('All');
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState('kanban'); // 'list' or 'kanban'
    const [activeLead, setActiveLead] = useState(null); // For drag overlay
    
    useEffect(() => {
        fetchData();
        if (['admin', 'superadmin', 'rop', 'partner', 'seller', 'employee', 'staff'].includes(currentUser?.role)) {
            fetchUsers();
        }
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await client.get('/crm/sellers');
            setUsers(res.data);
        } catch (err) {
            console.error('Error fetching sellers', err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await client.get('/crm/leads');
            setLeads(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error fetching leads', err);
            toast.error('Ошибка загрузки лидов');
        } finally {
            setLoading(false);
        }
    };

    const assignLead = async (leadId, userId) => {
        try {
            await client.post(`/crm/leads/${leadId}/assign?user_id=${userId}`);
            toast.success('Ответственный назначен');
            fetchData();
        } catch (err) {
            toast.error('Ошибка назначения');
        }
    };

    const claimLead = async (leadId) => {
        try {
            await client.post(`/crm/leads/${leadId}/claim`);
            toast.success('Лид взят в работу');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка взятия в работу');
        }
    };

    const updateLeadStatus = async (id, status) => {
        try {
            await client.patch(`/crm/leads/${id}`, { status });
            toast.success('Статус обновлен');
            fetchData();
        } catch (err) {
            toast.error('Ошибка обновления статуса');
        }
    };

    const convertLead = async (id) => {
        try {
            await client.post(`/crm/leads/${id}/convert`);
            toast.success('Конвертировано в Клуб');
            fetchData();
        } catch (err) {
            toast.error('Ошибка конвертации');
        }
    };

    const statusMap = {
        'All': 'Все',
        'New': 'Новые',
        'Processing': 'В работе',
        'Quality': 'Качественные',
        'Rejected': 'Отказ',
        'Archive': 'Архив'
    };

    const statuses = ['New', 'Processing', 'Quality', 'Rejected', 'Archive'];

    const filteredLeads = leads.filter(l => {
        const name = (l.first_name || '').toLowerCase();
        const username = (l.username || '').toLowerCase();
        const phone = (l.phone || '');
        const search = searchTerm.toLowerCase();
        
        const matchesSearch = name.includes(search) || username.includes(search) || phone.includes(search);
        const matchesTab = activeTab === 'All' || l.status === activeTab;
        
        return matchesSearch && matchesTab;
    });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveLead(leads.find(l => l.id === active.id));
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveLead(null);
        if (!over) return;

        const activeId = active.id;
        const overId = over.id; // column status

        // check if overId is one of our statuses
        if (statuses.includes(overId) && active.data.current.status !== overId) {
            updateLeadStatus(activeId, overId);
        }
    };

    const KanbanCard = ({ lead }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
            id: lead.id,
            data: { status: lead.status }
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1
        };

        return (
            <div 
                ref={setNodeRef} 
                style={style} 
                {...attributes} 
                {...listeners}
                onClick={() => setExpandedLeadId(lead.id)}
                className="bg-zinc-800/50 border border-white/5 p-4 rounded-2xl cursor-grab active:cursor-grabbing hover:border-blue-500/30 transition-all group"
            >
                <div className="flex justify-between items-start mb-2">
                    <span className="text-[8px] font-black uppercase text-zinc-500">{lead.source}</span>
                    {lead.rating > 0 && <span className="text-[10px] text-yellow-500 font-bold italic">★ {lead.rating}</span>}
                </div>
                <h4 className="text-sm font-bold text-white group-hover:text-blue-400 truncate">
                    {lead.first_name || 'Incognito'} {lead.last_name || ''}
                </h4>
                <div className="flex justify-between items-end mt-4">
                    <span className="text-[10px] text-zinc-600 truncate max-w-[100px]">{lead.assigned_to_id ? users.find(u => u.id === lead.assigned_to_id)?.fio : 'Свободный'}</span>
                    <span className="text-[9px] font-mono text-zinc-700">{new Date(lead.created_at).toLocaleDateString()}</span>
                </div>
            </div>
        );
    };

    const KanbanColumn = ({ status, label, leads }) => {
        const { setNodeRef } = useSortable({ id: status });
        return (
            <div className="flex-1 min-w-[300px] bg-zinc-900/20 rounded-[2rem] border border-white/5 p-6 flex flex-col h-full min-h-[600px]">
                <div className="flex justify-between items-center mb-6 px-2">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">{label}</h3>
                    <span className="bg-zinc-800 text-zinc-500 px-2.5 py-0.5 rounded-full text-[9px] font-black">{leads.length}</span>
                </div>
                <div ref={setNodeRef} className="flex-1 space-y-4">
                    <SortableContext items={leads.map(l => l.id)} strategy={verticalListSortingStrategy}>
                        {leads.map(lead => (
                            <KanbanCard key={lead.id} lead={lead} />
                        ))}
                    </SortableContext>
                </div>
            </div>
        );
    };

    const LeadRow = ({ lead }) => {
        const isExpanded = expandedLeadId === lead.id;
        const [activeDetailTab, setActiveDetailTab] = useState('profile'); // profile, chat, history, review
        const [aiAnalysis, setAiAnalysis] = useState(null);
        const [messages, setMessages] = useState([]);
        const [interactions, setInteractions] = useState([]);
        const [newMessage, setNewMessage] = useState('');
        const [loadingChat, setLoadingChat] = useState(false);
        const [loadingInteractions, setLoadingInteractions] = useState(false);
        
        // Editable fields local state
        const [editData, setEditData] = useState({
            first_name: lead.first_name || '',
            last_name: lead.last_name || '',
            phone: lead.phone || '',
            pains: lead.pains || '',
            objections: lead.objections || '',
            problems: lead.problems || '',
            rating: lead.rating || 0,
            review_text: lead.review_text || '',
            review_video_url: lead.review_video_url || '',
            total_check: lead.total_check || 0,
            notes: lead.notes || ''
        });

        const deleteLead = async (id) => {
            const confirmMsg = 'Вы уверены, что хотите окончательно удалить этого лида из CRM?';
            const performDelete = async () => {
                try {
                    await client.delete(`/crm/leads/${id}`);
                    toast.success('Лид удален');
                    fetchData();
                } catch (err) {
                    toast.error('Ошибка при удалении лида');
                }
            };

            const tg = window.Telegram?.WebApp;
            const isTelegram = !!tg?.initData;
            if (isTelegram && tg?.showConfirm) {
                tg.showConfirm(confirmMsg, (ok) => {
                    if (ok) performDelete();
                });
            } else {
                if (window.confirm(confirmMsg)) {
                    performDelete();
                }
            }
        };

        const updateDataMutation = useMutation({
            mutationFn: async (data) => (await client.patch(`/crm/leads/${lead.id}`, data)).data,
            onSuccess: () => {
                toast.success('Данные сохранены');
                fetchData();
            }
        });

        const analyzeMutation = useMutation({
            mutationFn: async (id) => (await client.post(`/ai/analyze-lead/${id}/`)).data,
            onSuccess: (data) => setAiAnalysis(data.analysis),
            onError: (err) => toast.error(err.response?.data?.detail || 'Ошибка AI анализа')
        });

        const fetchChat = async () => {
            setLoadingChat(true);
            try {
                const res = await client.get(`/crm/leads/${lead.id}/chat`);
                setMessages(res.data);
            } catch (err) {
                console.error('Error fetching chat', err);
            } finally {
                setLoadingChat(false);
            }
        };

        const fetchInteractions = async () => {
            setLoadingInteractions(true);
            try {
                const res = await client.get(`/crm/leads/${lead.id}/interactions`);
                setInteractions(res.data);
            } catch (err) {
                console.error('Error fetching interactions', err);
            } finally {
                setLoadingInteractions(false);
            }
        };

        const sendMessage = async () => {
            if (!newMessage.trim()) return;
            try {
                await client.post(`/crm/leads/${lead.id}/chat`, { text: newMessage });
                setNewMessage('');
                fetchChat();
                fetchInteractions();
            } catch (err) {
                toast.error('Ошибка отправки сообщения');
            }
        };

        useEffect(() => {
            if (isExpanded) {
                if (activeDetailTab === 'chat') fetchChat();
                if (activeDetailTab === 'history') fetchInteractions();
            }
        }, [isExpanded, activeDetailTab]);

        const TabButton = ({ id, label, icon: Icon }) => (
            <button
                onClick={() => setActiveDetailTab(id)}
                className={`flex items-center gap-2 px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 ${
                    activeDetailTab === id 
                    ? 'border-blue-500 text-blue-500 bg-blue-500/5' 
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
            >
                <Icon size={14} />
                {label}
            </button>
        );

        return (
            <div className={`group border-b border-white/5 transition-all duration-300 ${isExpanded ? 'bg-white/[0.03]' : 'hover:bg-white/[0.01]'}`}>
                {/* Header / Clickable section */}
                <div 
                    onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
                    className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                >
                    <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-2xl bg-zinc-800 border border-white/10 flex items-center justify-center text-blue-500 font-black text-xl shadow-2xl relative">
                            {lead.first_name?.[0] || 'L'}
                            {lead.rating > 0 && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center text-[10px] text-black border-2 border-zinc-900 font-bold">
                                    {lead.rating}
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">
                                {lead.first_name || 'Incognito'} {lead.last_name || ''}
                            </h3>
                            <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-[10px] font-black uppercase text-zinc-500 tracking-wider">
                                    {lead.username ? `@${lead.username}` : (
                                        <a 
                                            href={`tg://user?id=${lead.telegram_id}`} 
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-blue-400 hover:underline"
                                        >
                                            Без ника (ID:{lead.telegram_id})
                                        </a>
                                    )}
                                </span>
                                <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                                <span className="text-[10px] font-mono text-blue-500/50">{lead.bot_name || 'Бот не указан'}</span>
                                <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-md ${lead.source?.includes('DeepLink') ? 'bg-purple-500/10 text-purple-400' : 'bg-zinc-800 text-zinc-500'}`}>
                                    {lead.source || 'Telegram'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="hidden lg:flex flex-col items-end mr-4">
                            <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest mb-1">XP (Баллы)</span>
                            <span className="text-xs text-emerald-400 font-mono italic">{lead.club_points || 0}</span>
                        </div>

                        <div className="hidden lg:flex flex-col items-end mr-4">
                            <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mb-1">Чек</span>
                            <span className="text-xs text-white font-mono">{lead.total_check?.toLocaleString() || 0} ₽</span>
                        </div>

                        <div className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-[0.15em] border transition-all ${
                            lead.status === 'Quality' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                            lead.status === 'Rejected' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                            lead.status === 'Processing' ? 'bg-blue-600/10 text-blue-400 border-blue-600/20' :
                            'bg-zinc-800 text-zinc-500 border-white/5'
                        }`}>
                            {statusMap[lead.status] || lead.status}
                        </div>

                        {!lead.assigned_to_id && (
                            <div className="bg-amber-500/10 text-amber-500 border border-amber-500/20 px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest animate-pulse">
                                Свободный
                            </div>
                        )}

                        {(['admin', 'superadmin', 'rop', 'partner', 'seller', 'employee', 'staff'].includes(currentUser?.role)) && (
                            <div className="hidden sm:flex flex-col items-end min-w-[120px]">
                                <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mb-1">Ответственный</span>
                                <span className="text-xs text-zinc-400">{users.find(u => u.id === lead.assigned_to_id)?.fio || 'Не назначен'}</span>
                            </div>
                        )}

                        <div className={`p-2 rounded-xl transition-all ${isExpanded ? 'bg-blue-600 text-white' : 'text-zinc-600 hover:text-zinc-400 bg-white/5'}`}>
                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                    </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                    <div className="animate-in slide-in-from-top-2 duration-300">
                        {/* Internal Tabs */}
                        <div className="flex px-8 border-b border-white/5 bg-black/20">
                            <TabButton id="profile" label="Профиль" icon={User} />
                            <TabButton id="chat" label="Чат (Telegram)" icon={MessageSquare} />
                            <TabButton id="history" label="История" icon={Calendar} />
                            <TabButton id="review" label="Отзыв" icon={CheckCircle2} />
                        </div>

                        <div className="p-8">
                            {activeDetailTab === 'profile' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <div className="space-y-6">
                                        <section>
                                            <h4 className="text-[10px] font-black text-zinc-600 uppercase tracking-widest mb-4">Основные</h4>
                                            <div className="space-y-3">
                                                <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                                                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Имя</label>
                                                    <input 
                                                        type="text" 
                                                        value={editData.first_name}
                                                        onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                                                        className="bg-transparent border-none text-sm font-bold text-white w-full outline-none"
                                                    />
                                                </div>
                                                <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                                                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Фамилия</label>
                                                    <input 
                                                        type="text" 
                                                        value={editData.last_name}
                                                        onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                                                        className="bg-transparent border-none text-sm font-bold text-white w-full outline-none"
                                                    />
                                                </div>
                                                <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                                                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Телефон</label>
                                                    <input 
                                                        type="text" 
                                                        value={editData.phone}
                                                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                                                        className="bg-transparent border-none text-sm font-mono text-white w-full outline-none"
                                                    />
                                                </div>
                                                <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                                                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Рейтинг (0-10)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editData.rating}
                                                        onChange={(e) => setEditData({...editData, rating: e.target.value})}
                                                        className="bg-transparent border-none text-xl font-black text-yellow-500 w-full outline-none"
                                                    />
                                                </div>
                                                <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                                                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Сумма сделок (₽)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editData.total_check}
                                                        onChange={(e) => setEditData({...editData, total_check: e.target.value})}
                                                        className="bg-transparent border-none text-xl font-mono text-white w-full outline-none"
                                                    />
                                                </div>
                                            </div>
                                        </section>

                                        <section>
                                            <h4 className="text-[10px] font-black text-zinc-600 uppercase tracking-widest mb-4">Бизнес анкета</h4>
                                            <div className="space-y-2">
                                                <p className="text-xs text-zinc-500 flex justify-between font-bold"><span>Город:</span> <span className="text-zinc-300">{lead.city || '—'}</span></p>
                                                <p className="text-xs text-zinc-500 flex justify-between font-bold"><span>Ниша:</span> <span className="text-zinc-300">{lead.niche || '—'}</span></p>
                                                {lead.bio && (
                                                    <div className="pt-2 border-t border-white/5 mt-2">
                                                        <label className="text-[9px] font-black text-blue-500 uppercase mb-1 block">BIO из Telegram</label>
                                                        <p className="text-[11px] text-zinc-400 italic leading-relaxed">{lead.bio}</p>
                                                    </div>
                                                )}
                                                <p className="text-xs text-zinc-500 flex justify-between font-bold"><span>Оборот:</span> <span className="text-zinc-300">{lead.turnover || '—'}</span></p>
                                                <p className="text-xs text-zinc-500 flex justify-between font-bold"><span>Команда:</span> <span className="text-zinc-300">{lead.employees_count || '0'}</span></p>
                                                <div className="pt-2 border-t border-white/5 mt-2 space-y-1">
                                                    <p className="text-[10px] text-zinc-600 font-black uppercase tracking-widest">Источник клика</p>
                                                    <p className="text-xs text-zinc-300 font-mono bg-white/5 p-2 rounded-lg border border-white/5">
                                                        {lead.source || 'Telegram (Прямой)'}
                                                    </p>
                                                    {lead.referral_code && (
                                                        <div className="mt-2">
                                                            <p className="text-[10px] text-blue-500 font-black uppercase tracking-widest">Реферальный код</p>
                                                            <p className="text-xs text-blue-400 font-bold bg-blue-500/5 p-2 rounded-lg border border-blue-500/10">
                                                                {lead.referral_code}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </section>
                                    </div>

                                    <div className="space-y-6">
                                        <section>
                                            <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-4">Профайлинг</h4>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">Боли</label>
                                                    <textarea 
                                                        value={editData.pains}
                                                        onChange={(e) => setEditData({...editData, pains: e.target.value})}
                                                        className="w-full bg-white/5 border border-white/5 rounded-xl p-3 text-xs text-zinc-300 outline-none focus:border-blue-500 min-h-[60px]"
                                                        placeholder="Что болит у бизнеса..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">Возражения</label>
                                                    <textarea 
                                                        value={editData.objections}
                                                        onChange={(e) => setEditData({...editData, objections: e.target.value})}
                                                        className="w-full bg-white/5 border border-white/5 rounded-xl p-3 text-xs text-zinc-300 outline-none focus:border-blue-500 min-h-[60px]"
                                                        placeholder="Почему сомневается..."
                                                    />
                                                </div>
                                            </div>
                                        </section>
                                    </div>

                                    <div className="space-y-6">
                                        <section>
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest flex items-center gap-2">
                                                    <Sparkles size={12} /> AI Анализ
                                                </h4>
                                                <button 
                                                    onClick={() => analyzeMutation.mutate(lead.id)}
                                                    className="p-1.5 bg-emerald-500/10 text-emerald-500 rounded-lg"
                                                >
                                                    <RefreshCw size={12} className={analyzeMutation.isLoading ? 'animate-spin' : ''} />
                                                </button>
                                            </div>
                                            <div className="bg-white/5 border border-white/5 rounded-2xl p-4 text-[11px] text-zinc-400 leading-relaxed italic">
                                                {aiAnalysis || lead.extra_data?.ai_summary || "Анализ еще не проводился. Нажмите на 'Запуск' для генерации профиля."}
                                            </div>
                                        </section>
                                        <div className="flex flex-col gap-3">
                                            <button 
                                                onClick={() => updateDataMutation.mutate(editData)}
                                                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-blue-500 transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-2"
                                            >
                                                <Save size={14} /> Сохранить профиль
                                            </button>
                                            {['admin', 'superadmin', 'rop'].includes(currentUser?.role) && (
                                                <button 
                                                    onClick={() => deleteLead(lead.id)}
                                                    className="w-full py-4 bg-rose-600/10 text-rose-500 border border-rose-500/20 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center gap-2"
                                                >
                                                    <Trash2 size={14} /> Удалить лида
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeDetailTab === 'chat' && (
                                <div className="flex flex-col h-[500px]">
                                    <div className="flex-1 overflow-y-auto space-y-4 pr-2 mb-4 scrollbar-hide">
                                        {loadingChat ? (
                                            <div className="flex items-center justify-center h-full text-zinc-500 uppercase text-[10px] font-black">Загрузка переписки...</div>
                                        ) : messages.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center h-full text-zinc-600 opacity-30">
                                                <MessageSquare size={40} className="mb-2" />
                                                <span className="text-[10px] font-black uppercase">Нет сообщений</span>
                                            </div>
                                        ) : (
                                            messages.slice().reverse().map(msg => (
                                                <div key={msg.id} className={`flex ${msg.out ? 'justify-end' : 'justify-start'}`}>
                                                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-xs ${msg.out ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white/5 text-zinc-200 border border-white/5 rounded-tl-none'}`}>
                                                        <p>{msg.text}</p>
                                                        <span className="text-[8px] opacity-40 mt-1 block font-mono">
                                                            {new Date(msg.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                                            placeholder="Сообщение в Telegram..."
                                            className="flex-1 bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-xs text-white focus:border-blue-500 outline-none"
                                        />
                                        <button 
                                            onClick={sendMessage}
                                            className="px-6 bg-blue-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-2"
                                        >
                                            <Send size={14} /> Отправить
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeDetailTab === 'history' && (
                                <div className="space-y-4">
                                    {loadingInteractions ? (
                                        <div className="text-center py-10 text-zinc-500 text-[10px] font-black uppercase">Загрузка истории...</div>
                                    ) : interactions.length === 0 ? (
                                        <div className="text-center py-10 text-zinc-600 text-[10px] font-black uppercase">История пуста</div>
                                    ) : (
                                        <div className="relative border-l-2 border-white/5 ml-4 space-y-8 pl-8 py-4">
                                            {interactions.map(item => (
                                                <div key={item.id} className="relative">
                                                    <div className="absolute -left-[41px] top-0 w-5 h-5 bg-zinc-900 border-2 border-blue-600 rounded-full flex items-center justify-center">
                                                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-1">
                                                            <span className="text-[10px] font-black uppercase bg-blue-600/10 text-blue-500 px-2 py-0.5 rounded-md">{item.type}</span>
                                                            <span className="text-[9px] font-mono text-zinc-600">{new Date(item.created_at).toLocaleString()}</span>
                                                        </div>
                                                        <p className="text-sm text-zinc-300">{item.content}</p>
                                                        <span className="text-[9px] text-zinc-600 mt-1 block">Источник: {item.platform || 'System'}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === 'review' && (
                                <div className="max-w-2xl mx-auto space-y-6">
                                    <div className="bg-zinc-800/20 border border-white/5 rounded-3xl p-8">
                                        <h4 className="text-[10px] font-black text-yellow-500 uppercase tracking-widest mb-6">Сбор обратной связи</h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">Текст отзыва</label>
                                                <textarea 
                                                    value={editData.review_text}
                                                    onChange={(e) => setEditData({...editData, review_text: e.target.value})}
                                                    className="w-full bg-black/40 border border-white/5 rounded-2xl p-4 text-xs text-zinc-300 outline-none focus:border-yellow-500 min-h-[120px]"
                                                    placeholder="Что участник говорит о сообществе..."
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">Ссылка на видео (YouTube / Drive)</label>
                                                <div className="flex gap-2">
                                                    <input 
                                                        type="text" 
                                                        value={editData.review_video_url}
                                                        onChange={(e) => setEditData({...editData, review_video_url: e.target.value})}
                                                        className="flex-1 bg-black/40 border border-white/5 rounded-2xl px-6 py-4 text-xs text-zinc-300 outline-none focus:border-yellow-500"
                                                        placeholder="https://..."
                                                    />
                                                    {editData.review_video_url && (
                                                        <a href={editData.review_video_url} target="_blank" rel="noreferrer" className="p-4 bg-zinc-800 text-zinc-400 rounded-2xl hover:text-white">
                                                            <ExternalLink size={18} />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => updateDataMutation.mutate(editData)}
                                                className="w-full py-4 bg-yellow-600 text-black rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-yellow-500 transition-all mt-4"
                                            >
                                                Опубликовать отзыв
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Actions that were outside before */}
                {!isExpanded && (
                    <div className="px-4 pb-4 flex justify-end gap-2">
                         <button 
                            onClick={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, 'Quality'); }}
                            className="p-2 bg-emerald-500/10 text-emerald-500 rounded-xl hover:bg-emerald-500/20"
                        >
                            <Check size={16} />
                        </button>
                        {['admin', 'superadmin', 'rop'].includes(currentUser?.role) && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); deleteLead(lead.id); }}
                                className="p-2 bg-rose-500/10 text-rose-400 rounded-xl hover:bg-rose-500/20"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                        {!lead.assigned_to_id && ['seller', 'employee', 'staff', 'partner'].includes(currentUser?.role) && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); claimLead(lead.id); }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 shadow-lg shadow-blue-600/20"
                            >
                                Взять в работу
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
            {/* Header section with Stats */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <h1 className="text-4xl font-black text-white tracking-tighter flex items-center gap-4 italic uppercase">
                        <div className="w-14 h-14 bg-blue-600 rounded-[1.5rem] flex items-center justify-center shadow-2xl shadow-blue-600/20">
                            <Target className="w-8 h-8 text-white" />
                        </div>
                        CRM / База Лидов
                    </h1>
                    <p className="text-zinc-500 text-sm font-medium ml-1">Управление воронкой сообщества и квалификация участников</p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 group-focus-within:text-blue-500 transition-colors" />
                        <input 
                            type="text" 
                            placeholder="Поиск лида..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-zinc-900/50 border border-white/5 rounded-2xl pl-12 pr-6 py-4 text-sm text-white focus:border-blue-500 outline-none w-72 transition-all backdrop-blur-xl"
                        />
                    </div>
                    <button 
                        onClick={fetchData} 
                        className="p-4 bg-zinc-900/50 border border-white/5 rounded-2xl hover:bg-zinc-800 transition-all text-zinc-500 hover:text-white"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    {['admin', 'superadmin', 'rop', 'partner', 'seller', 'employee', 'staff'].includes(currentUser?.role) && (
                        <button 
                            onClick={() => setIsLeadModalOpen(true)}
                            className="p-4 bg-blue-600 border border-blue-500 rounded-2xl hover:bg-blue-500 transition-all text-white shadow-xl shadow-blue-600/20 flex items-center gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden md:inline">Добавить лида</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Quick Stats & Tabs */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex flex-wrap items-center gap-2 p-2 bg-zinc-900/50 rounded-[1.5rem] border border-white/5 backdrop-blur-md">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-3 rounded-xl transition-all ${viewMode === 'list' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Список"
                    >
                        <ListIcon size={18} />
                    </button>
                    <button
                        onClick={() => setViewMode('kanban')}
                        className={`p-3 rounded-xl transition-all ${viewMode === 'kanban' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Канбан"
                    >
                        <LayoutGrid size={18} />
                    </button>
                    <div className="w-px h-6 bg-white/5 mx-2" />
                    {Object.entries(statusMap).map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => { setActiveTab(key); setExpandedLeadId(null); setViewMode('list'); }}
                            className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                activeTab === key && viewMode === 'list'
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                                : 'text-zinc-500 hover:text-zinc-300'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-8 px-6 text-zinc-500">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black uppercase tracking-widest">Всего</span>
                        <span className="text-2xl font-black text-white italic">{leads.length}</span>
                    </div>
                    <div className="w-px h-8 bg-white/5" />
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Новых</span>
                        <span className="text-2xl font-black text-white italic">{leads.filter(l => l.status === 'New').length}</span>
                    </div>
                </div>
            </div>

            {/* Main List Container */}
            <div className={`${viewMode === 'kanban' ? '' : 'bg-zinc-900/30 border border-white/5 rounded-[2.5rem] overflow-hidden backdrop-blur-xl shadow-2xl'}`}>
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-40 gap-4">
                        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Загрузка базы данных...</p>
                    </div>
                ) : viewMode === 'kanban' ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                        <div className="flex gap-8 overflow-x-auto pb-10 scrollbar-hide">
                            {statuses.map(status => (
                                <KanbanColumn 
                                    key={status} 
                                    status={status} 
                                    label={statusMap[status]} 
                                    leads={filteredLeads.filter(l => l.status === status)} 
                                />
                            ))}
                        </div>
                        <DragOverlay>
                            {activeLead ? (
                                <div className="bg-blue-600 border border-blue-500 p-4 rounded-2xl shadow-2xl opacity-90 scale-105">
                                    <h4 className="text-sm font-bold text-white">
                                        {activeLead.first_name} {activeLead.last_name}
                                    </h4>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : filteredLeads.length > 0 ? (
                    <div className="divide-y divide-white/5">
                        {filteredLeads.map(lead => (
                            <LeadRow key={lead.id} lead={lead} />
                        ))}
                    </div>
                ) : (
                    <div className="py-40 text-center">
                        <div className="w-20 h-20 bg-zinc-800/50 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                            <Users size={32} className="text-zinc-700" />
                        </div>
                        <h3 className="text-xl font-black text-zinc-400 uppercase tracking-tighter italic">Лиды не найдены</h3>
                        <p className="text-zinc-600 text-sm mt-2 max-w-sm mx-auto">Попробуйте изменить параметры фильтрации или поиска</p>
                    </div>
                )}
            </div>
            <LeadModal 
                isOpen={isLeadModalOpen} 
                onClose={() => setIsLeadModalOpen(false)} 
                onSuccess={() => {
                    setIsLeadModalOpen(false);
                    fetchData();
                }}
            />
        </div>
    );
};

export default CRM;
